import React, { useState } from 'react';
import { Event, EventBase } from '../../../../../model/event';
import { Card } from '../../../../../components/Card/Card';
import { PermissionButton } from '../../../../../components/Button/PermissionButton';
import { formatDateTime, present } from '../../../../../util/util';
import { KeyValueTable } from '../../../../../components/KeyValueTable/KeyValueTable';
import { EventForm } from '../../EventForm/EventForm';
import { ControlledModal } from '../../../../../components/Modal/ControlledModal';
import { t } from '../../../../../locales/translate';
import { camelCase } from 'lodash';

interface Props {
  event: Event;
}

export function GeneralInfo({ event }: Props): JSX.Element {
  const [modal, setModal] = useState(false);

  return (
    <section>
      <Card
        title={t.general.generalInformation}
        actions={
          <PermissionButton
            name="edit"
            title={t.model.ticket.updateGeneralInformation}
            model={event}
            permission="update_base_attributes"
            onClick={() => setModal(true)}
          />
        }
      >
        <KeyValueTable
          items={[
            {
              key: t.general.slug,
              value: event.slug,
              style: { height: '40px' },
            },
            {
              key: t.model.event.externalReference,
              value: event.externalReference,
            },
            { key: `${t.model.ticket.ticketline} 1`, value: event.artist },
            { key: `${t.model.ticket.ticketline} 2`, value: event.name },
            {
              key: `${t.model.ticket.ticketline} 3`,
              value: event.ticketSupportLine,
            },
            {
              key: `${t.model.ticket.ticketline} ${t.general.headline}`,
              value: event.ticketHeadline,
            },
            {
              key: `${t.model.ticket.ticketline} ${t.general.footer}`,
              value: event.additionalInformation && event.additionalInformation.title,
            },
            { key: t.model.event.tourOperator, value: event.tourOrganizer },
            {
              key: t.model.event.localOperator,
              value: event.localOrganizer,
              style: { height: '40px' },
            },
            {
              key: t.model.datetime.startDate,
              value: formatDateTime(event.happeningFrom),
            },
            ...(event.happeningUntil !== event.happeningFrom
              ? [
                  {
                    key: t.model.datetime.endDate,
                    value: formatDateTime(event.happeningUntil),
                  },
                ]
              : []),
            {
              key: t.model.event.admission,
              value: event.doorTime,
            },
            {
              key: t.model.event.stageTime,
              value: event.stageTime,
              style: { height: '40px' },
            },
            {
              key: t.model.event.personalization._name,
              value: event.personalizationRequirement
                ? t.model.event.personalization[camelCase(event.personalizationRequirement)] ||
                  event.personalizationRequirement
                : undefined,
            },
            {
              key: t.model.datetime.publishDate,
              value: event.publishedFrom ? formatDateTime(event.publishedFrom) : t.general.notAvailable,
              style: { height: '40px' },
            },
            {
              key: t.model.datetime.unPublishDate,
              value: event.publishedUntil ? formatDateTime(event.publishedUntil) : t.general.notAvailable,
              style: { height: '40px' },
            },
            {
              key: t.general.description,
              value: event.description,
            },
            {
              key: t.model.event.excludeFromFeeds,
              value: event.excludeFromFeeds ? t.general.yes : t.general.no,
            },
            present(event.legacyId) && {
              key: t.general.legacyId,
              value: event.legacyId,
            },
          ]}
        />
      </Card>
      <ControlledModal<EventBase>
        open={modal}
        model={event}
        onClose={() => setModal(false)}
        title={t.actions.edit(t.model.event._name)}
        size="small"
      >
        {(controller) => <EventForm controller={controller} />}
      </ControlledModal>
    </section>
  );
}
